import { Injectable, inject } from '@angular/core';
import { Observable, from, map, catchError, of, switchMap } from 'rxjs';
import { PocketbaseService } from './pocketbase.service';

export interface CaseDiscussion {
  id: string;
  case: string;
  user: string;
  text: string;
  parent_id?: string;
  likes_count?: number;
  created: string;
  updated: string;
  expand?: {
    user?: {
      id: string;
      username: string;
      name: string;
      avatar?: string;
    };
  };
}

export interface DiscussionStats {
  caseId: string;
  count: number;
}

@Injectable({
  providedIn: 'root'
})
export class CaseDiscussionsService {
  private readonly pocketbaseService = inject(PocketbaseService);

  /**
   * Get all discussions for a specific case
   */
  getDiscussionsByCase(caseId: string): Observable<CaseDiscussion[]> {
    return from(
      this.pocketbaseService.getPocketBase()
        .collection('case_discussions')
        .getFullList<CaseDiscussion>({
          filter: `case = "${caseId}"`,
          sort: '-created',
          expand: 'user'
        })
    ).pipe(
      catchError(error => {
        console.error('Failed to load discussions:', error);
        return of([]);
      })
    );
  }

  /**
   * Get discussion count for a specific case
   */
  getDiscussionCount(caseId: string): Observable<number> {
    return from(
      this.pocketbaseService.getPocketBase()
        .collection('case_discussions')
        .getList<CaseDiscussion>(1, 1, {
          filter: `case = "${caseId}"`
        })
    ).pipe(
      map(result => result.totalItems),
      catchError(() => of(0))
    );
  }

  /**
   * Get discussion counts for multiple cases (batch)
   */
  getDiscussionCounts(caseIds: string[]): Observable<Map<string, number>> {
    // For now, we'll do individual requests. Could be optimized with a custom endpoint later.
    const countsMap = new Map<string, number>();
    
    const requests = caseIds.map(caseId =>
      this.getDiscussionCount(caseId).pipe(
        map(count => ({ caseId, count }))
      )
    );

    return from(Promise.all(requests.map(req => req.toPromise()))).pipe(
      map(results => {
        results.forEach(result => {
          if (result) {
            countsMap.set(result.caseId, result.count);
          }
        });
        return countsMap;
      })
    );
  }

  /**
   * Create a new discussion/comment
   */
  createDiscussion(caseId: string, text: string, parentId?: string): Observable<CaseDiscussion> {
    const userId = this.pocketbaseService.getPocketBase().authStore.model?.['id'];
    
    if (!userId) {
      throw new Error('User must be authenticated to create a discussion');
    }

    const data = {
      case: caseId,
      user: userId,
      text: text.trim(),
      parent_id: parentId || '',
      likes_count: 0
    };

    return from(
      this.pocketbaseService.getPocketBase()
        .collection('case_discussions')
        .create<CaseDiscussion>(data, { expand: 'user' })
    );
  }

  /**
   * Update a discussion (only by the author)
   */
  updateDiscussion(discussionId: string, text: string): Observable<CaseDiscussion> {
    return from(
      this.pocketbaseService.getPocketBase()
        .collection('case_discussions')
        .update<CaseDiscussion>(discussionId, { text: text.trim() }, { expand: 'user' })
    );
  }

  /**
   * Delete a discussion (only by the author)
   */
  deleteDiscussion(discussionId: string): Observable<boolean> {
    return from(
      this.pocketbaseService.getPocketBase()
        .collection('case_discussions')
        .delete(discussionId)
    ).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  /**
   * Increment likes count (for future use)
   */
  likeDiscussion(discussionId: string, currentCount: number): Observable<CaseDiscussion> {
    return from(
      this.pocketbaseService.getPocketBase()
        .collection('case_discussions')
        .update<CaseDiscussion>(discussionId, { likes_count: currentCount + 1 })
    );
  }

  /**
   * Update likes count for a discussion
   */
  updateLikesCount(discussionId: string, liked: boolean): Observable<CaseDiscussion> {
    // First, get the current discussion to know its likes_count
    return from(
      this.pocketbaseService.getPocketBase()
        .collection('case_discussions')
        .getOne<CaseDiscussion>(discussionId)
    ).pipe(
      switchMap(discussion => {
        const currentCount = discussion.likes_count || 0;
        const newCount = liked ? currentCount + 1 : Math.max(0, currentCount - 1);
        
        return from(
          this.pocketbaseService.getPocketBase()
            .collection('case_discussions')
            .update<CaseDiscussion>(discussionId, { likes_count: newCount }, { expand: 'user' })
        );
      }),
      catchError(() => from(
        this.pocketbaseService.getPocketBase()
          .collection('case_discussions')
          .getOne<CaseDiscussion>(discussionId, { expand: 'user' })
      ))
    );
  }
}

